import React, { useMemo, useEffect, useRef } from 'react';
import { Bone, Loader2 } from 'lucide-react';
import { GameObject } from '../../../core/types/gameobject.types';
import { SpineRendererData, SpineAnimatorData } from '../../../core/types/spine.types';
import { SpineDebugOverlay } from './SpineDebugOverlay';
import { useSpineRuntimeStore } from '../../store/spineStore';
import { useSpinePreview } from '../../hooks/useSpinePreview';
import { useSpineCanvas } from '../../hooks/useSpineCanvas';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { useLivePreviewStore } from '../../store/livePreviewStore';
import { discoverAndRegisterSpineAsset } from '../../utils/spine-discovery';

interface SpineObjectRendererProps {
  gameObject: GameObject;
  screenX: number;
  screenY: number;
  width: number;
  height: number;
  zoom: number;
  isSelected: boolean;
  isHovered: boolean;
}

export const SpineObjectRenderer: React.FC<SpineObjectRendererProps> = ({
  gameObject,
  screenX,
  screenY,
  width,
  height,
  zoom,
  isSelected,
  isHovered,
}) => {
  const loadedAssets = useSpineRuntimeStore((s) => s.loadedAssets);

  // Find Spine components
  const spineRenderer = useMemo(() => {
    const component = gameObject.components.find((c) => c.type === 'spine-renderer');
    return component?.data as SpineRendererData | undefined;
  }, [gameObject.components]);

  const spineAnimator = useMemo(() => {
    const component = gameObject.components.find((c) => c.type === 'spine-animator');
    return component?.data as SpineAnimatorData | undefined;
  }, [gameObject.components]);

  // Get asset info — try by ID first, fall back to matching by skeletonPath
  const spineAsset = useMemo(() => {
    if (!spineRenderer?.spineAssetId) return null;
    const byId = loadedAssets[spineRenderer.spineAssetId];
    if (byId) return byId;
    if (spineRenderer.spineAssetPath) {
      const byPath = Object.values(loadedAssets).find(
        (a) => a.skeletonPath === spineRenderer.spineAssetPath
      );
      if (byPath) return byPath;
    }
    return null;
  }, [spineRenderer?.spineAssetId, spineRenderer?.spineAssetPath, loadedAssets]);

  // Auto-re-register spine asset after page reload
  const reregisteredRef = useRef<string | null>(null);
  useEffect(() => {
    const assetPath = spineRenderer?.spineAssetPath;
    if (!assetPath || spineAsset) return;
    if (reregisteredRef.current === assetPath) return;
    reregisteredRef.current = assetPath;

    const { assets } = useProjectPanelStore.getState();
    const skelAsset = Object.values(assets).find((a) => a.path === assetPath);
    if (!skelAsset) return;

    discoverAndRegisterSpineAsset(skelAsset).catch(() => {});
  }, [spineRenderer?.spineAssetPath, spineAsset]);

  // When live preview is playing, mirror the animation in the scene view (Unity-style)
  const isLivePlaying = useLivePreviewStore((s) => s.playState === 'playing');

  const effectiveIsPlaying = !!(spineAnimator?.isPlaying
    || (isLivePlaying && spineAnimator?.playOnAwake));
  const effectiveAnimation = spineAnimator?.currentAnimation
    || (isLivePlaying && spineAnimator?.playOnAwake ? spineAnimator?.defaultAnimation : null)
    || null;

  // Static snapshot (when not playing) — uses offscreen renderer + blob URL
  const { previewUrl, loading, width: skelWidth, height: skelHeight } = useSpinePreview({
    spineAsset,
    skinName: spineRenderer?.skinName || 'default',
    animationName: effectiveAnimation,
    isPlaying: false, // Never animate via image extraction — canvas handles that
    loop: spineAnimator?.defaultLoop ?? true,
    timeScale: spineAnimator?.speed ?? 1,
    mixDuration: spineAnimator?.defaultMixDuration ?? 0.2,
  });

  // Live canvas animation (when playing) — renders Spine directly at 60fps
  const { containerRef } = useSpineCanvas({
    spineAsset,
    skinName: spineRenderer?.skinName || 'default',
    animationName: effectiveAnimation,
    isPlaying: effectiveIsPlaying,
    loop: spineAnimator?.defaultLoop ?? true,
    timeScale: spineAnimator?.speed ?? 1,
    mixDuration: spineAnimator?.defaultMixDuration ?? 0.2,
  });

  // Use skeleton dimensions if available, otherwise fall back to passed dimensions
  const displayW = skelWidth > 0 ? skelWidth * zoom : width;
  const displayH = skelHeight > 0 ? skelHeight * zoom : height;

  return (
    <div
      style={{
        position: 'absolute',
        left: screenX - displayW / 2,
        top: screenY - displayH / 2,
        width: displayW,
        height: displayH,
        transform: `rotate(${gameObject.transform.rotation}deg)`,
        transformOrigin: 'center center',
        opacity: spineRenderer?.alpha ?? 1,
      }}
    >
      {/* Static preview image — kept in DOM always, hidden when canvas is active */}
      {previewUrl && (
        <img
          src={previewUrl}
          className="w-full h-full object-contain"
          draggable={false}
          alt={spineAsset?.name || 'Spine'}
          style={effectiveIsPlaying ? { visibility: 'hidden' } : undefined}
        />
      )}

      {/* Live canvas — overlaid on top when playing, renders Spine at 60fps */}
      {effectiveIsPlaying && (
        <div
          ref={containerRef}
          className="absolute inset-0"
        />
      )}

      {/* Loading state */}
      {!previewUrl && loading && (
        <div
          className={`w-full h-full flex flex-col items-center justify-center rounded-lg ${
            isSelected
              ? 'ring-2 ring-purple-500 bg-purple-500/20'
              : isHovered
              ? 'ring-1 ring-purple-400/50 bg-purple-500/10'
              : 'bg-zinc-800/50'
          }`}
        >
          <Loader2
            className="text-purple-400 animate-spin"
            style={{
              width: Math.max(20, width * 0.3),
              height: Math.max(20, height * 0.3),
            }}
          />
          <div className="text-xs text-zinc-400 mt-1">Loading...</div>
        </div>
      )}

      {/* Placeholder when no asset */}
      {!previewUrl && !loading && (
        <div
          className={`w-full h-full flex flex-col items-center justify-center rounded-lg transition-all ${
            isSelected
              ? 'ring-2 ring-purple-500 bg-purple-500/20'
              : isHovered
              ? 'ring-1 ring-purple-400/50 bg-purple-500/10'
              : 'bg-zinc-800/50'
          }`}
          style={{ borderColor: spineRenderer?.tint || '#ffffff' }}
        >
          <Bone
            className="text-purple-400"
            style={{
              width: Math.max(24, width * 0.4),
              height: Math.max(24, height * 0.4),
            }}
          />
          <div className="text-xs text-zinc-400 mt-1 text-center truncate max-w-full px-1">
            {spineAsset?.name || 'No Asset'}
          </div>
          {spineRenderer?.skinName && spineRenderer.skinName !== 'default' && (
            <div className="text-[10px] text-purple-400 truncate max-w-full px-1">
              {spineRenderer.skinName}
            </div>
          )}
          {spineAnimator?.currentAnimation && (
            <div className="text-[10px] text-green-400 truncate max-w-full px-1 flex items-center gap-1">
              {spineAnimator.isPlaying && (
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              )}
              {spineAnimator.currentAnimation}
            </div>
          )}
        </div>
      )}

      {/* Selection ring */}
      {(previewUrl || effectiveIsPlaying) && (isSelected || isHovered) && (
        <div
          className={`absolute inset-0 rounded-lg pointer-events-none ${
            isSelected
              ? 'ring-2 ring-purple-500'
              : 'ring-1 ring-purple-400/50'
          }`}
        />
      )}

      {/* Debug Overlays */}
      {spineRenderer?.showBones && spineAsset && (
        <SpineDebugOverlay
          type="bones"
          asset={spineAsset}
          width={displayW}
          height={displayH}
          zoom={zoom}
        />
      )}
      {spineRenderer?.showSlots && spineAsset && (
        <SpineDebugOverlay
          type="slots"
          asset={spineAsset}
          width={displayW}
          height={displayH}
          zoom={zoom}
        />
      )}
      {spineRenderer?.showBoundingBoxes && spineAsset && (
        <SpineDebugOverlay
          type="boundingBoxes"
          asset={spineAsset}
          width={displayW}
          height={displayH}
          zoom={zoom}
        />
      )}
    </div>
  );
};
